import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  CopyToClipboard,
  Modal,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
  TextContent,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { dissociateGlossaryFromResource, listResourcesAssociatedToMetadataEntity } from 'src/api/catalog';
import { defaultWrapLinesPreference, largePageSizePreference, paginationLabels } from 'src/commons/tables';
import { convertResourceARNToLink, getScopeForArn } from 'src/components/utils/arnUtil';
import { Link } from 'src/components/common/Link';
import { AUTO_INHERIT, HC_RESOURCE_ARN_PREFIX, METADATA_FORM_PREFIX, USER_EXPLICIT } from 'src/commons/constants';
import { EmptyState } from 'src/commons/EmptyState';

import { PageHeader } from 'src/components/common/PageHeader';
import {
  DissociateGlossaryFromResourceResponse,
  GlossaryDetails,
} from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';
import { getDistinctObjectsFor2Keys } from 'src/components/workspaces/common/common';
import CsvDownloaderWrapper, { generateCsvColumnDefinitions } from 'src/components/common/csvDownloaderWrapper';
import {
  flattenGlossaryResourceAssociation,
  generateCsvFileName,
  getCsvDataForGlossaryResources,
  getGlossaryAssociationType,
} from 'src/commons/common';
import _ from 'lodash';
import { getResourceNameFromArn } from 'src/components/dataadvisory/constants';
import { createMetadataFormViewLink } from 'src/routes';

export interface BusinessGlossaryAssociationsTableProps {
  glossary: GlossaryDetails;
  childGovernanceTagGlossaries?: GlossaryDetails[];
  enableCSVDownload?: boolean;
  ownerId: string;
  setNotification: (header: any, message: string) => void;
  activeWorkspace?: any;
  isGovernanceTag?: boolean;
  refreshAssociations?: boolean;
}

const MAX_RES_8000 = 8000;
const BusinessGlossaryAssociationsTable = (props: BusinessGlossaryAssociationsTableProps) => {
  const [glossaryAssociations, setGlossaryAssociations] = useState([]);
  const [selectedAssociatedResources, setSelectedAssociatedResources] = useState([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [loadingGlossaryAssociations, setLoadingGlossaryAssociations] = useState(false);
  const [dissociateGlossaryModalVisible, setDissociateGlossaryModalVisible] = useState(false);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const glossaryId = props.glossary.GlossaryId;
  const governanceTagName = props.glossary.GlossaryValue;
  const glossaryOwnerId = props.glossary.OwnerId;
  const childGovernanceTagGlossaries = props.childGovernanceTagGlossaries;
  const enableCSVDownload: boolean = props.enableCSVDownload;
  const isGovernanceTag: boolean = props?.isGovernanceTag;
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });
  const [csvColumnDefinition, setCsvColumnDefinition] = useState([]);
  const [csvData, setCsvData] = useState([]);

  const refForCSVDownloader: React.RefObject<any> = React.createRef();
  const onClickDownload = () => {
    refForCSVDownloader.current.handleClick();
  };

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(
    glossaryAssociations == undefined ? [] : glossaryAssociations,
    {
      filtering: {
        noMatch: '',
        empty: <EmptyState title={'No associations'} subtitle={'No associated resources were found.'} />,
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {},
      selection: {},
      propertyFiltering: {
        filteringProperties: [],
      },
    },
  );

  const generateResourceARNLink = (item: any) => {
    const arn = item.Resource;
    if (arn.startsWith(METADATA_FORM_PREFIX)) {
      return createMetadataFormViewLink(arn, item.OwnerId);
    }
    if (arn.startsWith(HC_RESOURCE_ARN_PREFIX)) {
      return convertResourceARNToLink(item.Resource);
    }
  };

  const glossaryAssociationColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'resourceName',
      header: 'Resource name',
      cell: (item) => <Link href={generateResourceARNLink(item)}>{getResourceNameFromArn(item.Resource)}</Link>,
      minWidth: 150,
    },
    {
      id: 'resourceType',
      header: 'Resource type',
      cell: (item) => {
        if (item.Resource?.startsWith(METADATA_FORM_PREFIX)) {
          return 'Metadata form';
        }
        return getScopeForArn(item.Resource);
      },
      minWidth: 200,
    },
    {
      id: 'assignmentType',
      header: 'Assignment type',
      cell: (item) => getGlossaryAssociationType(item),
      minWidth: 150,
      sortingField: 'AssociationAssignmentType',
    },
    {
      id: 'resourceArn',
      header: 'Resource ARN',
      cell: (item) => (
        <CopyToClipboard
          textToCopy={item.Resource}
          variant={'inline'}
          copySuccessText={'Successfully copied ARN'}
          copyErrorText={'Failed to copy ARN'}
        />
      ),
      minWidth: 400,
      sortingField: 'Resource',
    },
  ];

  useEffect(() => {
    const { selectedItems } = collectionProps;
    setSelectedAssociatedResources(selectedItems as any[]);
  }, [collectionProps.selectedItems]);

  useEffect(() => {
    getGlossaryAssociations();
  }, [glossaryId, props?.refreshAssociations]);

  const getGlossaryAssociationsPage = async (
    glossaryId: string,
    glossaryOwnerId: string,
    nextToken: string | undefined,
    maxResults: number,
  ): Promise<{ associations: any[]; nextToken: string }> => {
    let glossaryAssociations = [];
    let result = await listResourcesAssociatedToMetadataEntity({
      OwnerId: glossaryOwnerId,
      Id: glossaryId,
      NextToken: nextToken,
      MaxResults: maxResults,
    });
    glossaryAssociations.push(
      ...result.FormList,
      ...result.TableList,
      ...result.CatalogList,
      ...result.DatabaseList,
      ...result.ClusterList,
      ...result.ColumnList,
      ...result.SchemaList,
      ...result.FineGrainAccessPolicyList,
    );
    return {
      associations: glossaryAssociations,
      nextToken: result.NextToken,
    };
  };

  const getGlossaryAssociations = async () => {
    setLoadingGlossaryAssociations(true);
    setAllDataLoaded(false);
    setGlossaryAssociations([]);
    try {
      // get all glossaries for owner and filter on the results
      let assns = [];
      // run the child association concurrently - belongs to different catalogs, teams
      const promises = childGovernanceTagGlossaries.map(async (childGovernanceTag) => {
        let localNextToken = null;
        const associations = [];
        do {
          const { associations: pageAssociations, nextToken } = await getGlossaryAssociationsPage(
            childGovernanceTag.GlossaryId,
            childGovernanceTag.OwnerId,
            localNextToken,
            MAX_RES_8000,
          );
          associations.push(...pageAssociations);
          localNextToken = nextToken;
        } while (!!localNextToken);
        return associations;
      });

      const allAssociations = await Promise.all(promises);
      const items = allAssociations.flat();
      assns.push(...items);
      // paginate and fetch using nextToken until one result is available.  Else the first page shows 0, but on nextPage fetch it shows
      // results
      let localNextToken = null;
      do {
        const { associations, nextToken: nextTokeRes } = await getGlossaryAssociationsPage(
          glossaryId,
          glossaryOwnerId,
          localNextToken,
          MAX_RES_8000,
        );
        assns.push(...associations);
        setNextToken(nextTokeRes);
        localNextToken = nextTokeRes;
        // loop for all resource associations for governance tags, but stop for first page for business glossaries.
      } while (isGovernanceTag ? localNextToken != null : assns.length == 0 && localNextToken != null);

      if (localNextToken == null) {
        setAllDataLoaded(true);
      }
      let distinctAssociations = getDistinctObjectsFor2Keys(assns, 'Resource', 'AssociationAssignmentType');
      // filter and take only UserExplicit if both types are present
      distinctAssociations = getUserExplicitAssociationForConflicts(distinctAssociations);
      setGlossaryAssociations(distinctAssociations);
      prepareCSVDownloadData(distinctAssociations);
    } catch (err) {
      props.setNotification('error', `Unable to load glossaries. ${err.message}`);
    } finally {
      setLoadingGlossaryAssociations(false);
      setSelectedAssociatedResources([]);
    }
  };

  const getUserExplicitAssociationForConflicts = (associations: any[]): any[] => {
    // filter and take only UserExplicit if both types are present
    return associations?.filter((item) => {
      if (item.AssociationAssignmentType == USER_EXPLICIT) {
        return true;
      }
      if (item.AssociationAssignmentType == AUTO_INHERIT) {
        return (
          associations.find((i) => i.Resource == item.Resource && i.AssociationAssignmentType == USER_EXPLICIT) ==
          undefined
        );
      }
    });
  };

  const prepareCSVDownloadData = (distinctAssociations: any[]) => {
    if (enableCSVDownload) {
      let childGovTags = !!props.childGovernanceTagGlossaries ? props.childGovernanceTagGlossaries : [];
      let csvData = distinctAssociations
        .map((item) => flattenGlossaryResourceAssociation(item, [props.glossary, ...childGovTags]))
        .filter((item) => !_.isEmpty(item))
        .map((item) => getCsvDataForGlossaryResources(item));
      setCsvData(csvData);
      setCsvColumnDefinition(generateCsvColumnDefinitions(csvData));
    }
  };

  const loadMoreGlossaryAssociations = async () => {
    setLoadingGlossaryAssociations(true);
    setGlossaryAssociations([]);
    try {
      const { associations, nextToken: nextTokeRes } = await getGlossaryAssociationsPage(
        glossaryId,
        glossaryOwnerId,
        nextToken,
        MAX_RES_8000,
      );
      let currentGlossaryAssociations = glossaryAssociations;
      currentGlossaryAssociations.push(...associations);
      const distinctAssns = getUserExplicitAssociationForConflicts(
        getDistinctObjectsFor2Keys(currentGlossaryAssociations, 'Resource', 'AssociationAssignmentType'),
      );
      setGlossaryAssociations(distinctAssns);
      setNextToken(nextTokeRes);
      if (nextTokeRes == null) {
        setAllDataLoaded(true);
      }
    } catch (err) {
      props.setNotification('error', `Unable to load glossaries. ${err.message}`);
    } finally {
      setLoadingGlossaryAssociations(false);
      setSelectedAssociatedResources([]);
    }
  };

  const handlePageChange = async (e) => {
    if (paginationProps.currentPageIndex >= paginationProps.pagesCount && !allDataLoaded) {
      await loadMoreGlossaryAssociations();
    }
    if (
      paginationProps.currentPageIndex != paginationProps.pagesCount ||
      (e.detail.currentPageIndex <= paginationProps.pagesCount &&
        e.detail.currentPageIndex != paginationProps.currentPageIndex)
    ) {
      paginationProps.onChange(e);
    }
  };

  const handleDissociateGlossaryFromResource = async () => {
    setButtonLoading(true);
    try {
      const hasAutoInheritSelectionCount = selectedAssociatedResources.filter(
        (res) => res.AssociationAssignmentType && res.AssociationAssignmentType == AUTO_INHERIT,
      ).length;
      if (hasAutoInheritSelectionCount > 0) {
        props.setNotification(
          'error',
          `Cannot dissociate ${hasAutoInheritSelectionCount} resource(s) with auto-inherit assignment type.`,
        );
        return;
      }
      // run 5 concurrent dissociations at once
      const allDissnResults: DissociateGlossaryFromResourceResponse[] = [];
      for (let i = 0; i < selectedAssociatedResources.length; i += 5) {
        const batch = selectedAssociatedResources.slice(i, i + 5);
        const results = await Promise.all(
          batch.map(
            async (selectedAssociatedResource) =>
              await dissociateGlossaryFromResource({
                ResourceArn: selectedAssociatedResource?.Resource,
                GlossaryId: selectedAssociatedResource?.GlossaryId,
              }),
          ),
        );
        results.forEach((res) => allDissnResults.push(res));
      }
      const successCount = allDissnResults
        .map((res) => res?.Message?.toLowerCase())
        .filter((msg) => msg == 'success').length;
      if (successCount == allDissnResults.length) {
        props.setNotification('success', `Successfully dissociated ${successCount} resource(s)!`);
      } else {
        props.setNotification(
          'error',
          `Failure in dissociating  ${allDissnResults.length - successCount} resource(s)!`,
        );
      }
    } catch (err) {
      props.setNotification('error', `Error in dissociating resources: ${err.message}`);
    } finally {
      setButtonLoading(false);
      setDissociateGlossaryModalVisible(false);
      await getGlossaryAssociations();
    }
  };

  const glossaryAssociationTableButtonOptions = () => {
    return [
      {
        text: '',
        icon: 'refresh',
        onItemClick: getGlossaryAssociations,
      },
      {
        text: 'Dissociate',
        variant: 'normal',
        disabled:
          !selectedAssociatedResources ||
          selectedAssociatedResources.length == 0 ||
          selectedAssociatedResources.filter((res) => res.GlossaryId !== glossaryId).length > 0 ||
          selectedAssociatedResources.filter((res) => res.AssociationAssignmentType == AUTO_INHERIT).length ==
            selectedAssociatedResources.length,
        onItemClick: () => setDissociateGlossaryModalVisible(true),
      },
    ];
  };

  const getFilterCounterText = (count) => `${count} ${count === 1 ? 'match' : 'matches'}`;

  return (
    <>
      <Modal
        visible={dissociateGlossaryModalVisible}
        header={[`Confirm deletion`]}
        onDismiss={() => setDissociateGlossaryModalVisible(false)}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button variant='primary' loading={buttonLoading} onClick={handleDissociateGlossaryFromResource}>
                Confirm
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <TextContent>
          Are you sure you want to dissociate <strong>{selectedAssociatedResources?.length}</strong> resources?
          <br />
        </TextContent>
      </Modal>
      <SpaceBetween size='l'>
        <Table
          {...collectionProps}
          selectionType='multi'
          loadingText='Loading glossaries...'
          loading={loadingGlossaryAssociations}
          columnDefinitions={glossaryAssociationColumnDefinitions}
          items={items}
          wrapLines={preferences.wrapLines}
          empty={
            <Box textAlign='center' color='inherit'>
              <b>No associations</b>
              <Box variant='p' color='inherit'>
                No associations to display.
              </Box>
            </Box>
          }
          filter={
            <TextFilter
              {...filterProps}
              filteringAriaLabel='Filter resources'
              filteringPlaceholder='Find resources'
              disabled={loadingGlossaryAssociations}
              countText={getFilterCounterText(filteredItemsCount)}
            />
          }
          resizableColumns={true}
          header={
            <PageHeader
              counter={`(${glossaryAssociations.length}${nextToken ? '+' : ''})`}
              buttons={glossaryAssociationTableButtonOptions()}
              header={'Resource associations'}
              additionalItems={
                enableCSVDownload &&
                !loadingGlossaryAssociations && (
                  <>
                    <Button
                      variant='primary'
                      disabled={!csvData || csvData.length == 0}
                      onClick={onClickDownload}
                      iconName='download'
                    >
                      Download as CSV
                    </Button>
                    <CsvDownloaderWrapper
                      rowItems={csvData}
                      fileName={generateCsvFileName(`governance-tag-${governanceTagName}`, null)}
                      refForCsvDownloader={refForCSVDownloader}
                      userFriendlyDataDefinitions={csvColumnDefinition}
                    />
                  </>
                )
              }
            />
          }
          preferences={
            <CollectionPreferences
              title={'Preferences'}
              confirmLabel={'Confirm'}
              cancelLabel={'Cancel'}
              preferences={preferences}
              onConfirm={({ detail }) => setPreferences(detail)}
              pageSizePreference={largePageSizePreference}
              wrapLinesPreference={defaultWrapLinesPreference}
            />
          }
          pagination={
            <Pagination
              {...paginationProps}
              ariaLabels={paginationLabels}
              onChange={(e) => handlePageChange(e)}
              openEnd={!allDataLoaded}
              disabled={loadingGlossaryAssociations}
            />
          }
        />
      </SpaceBetween>
    </>
  );
};

export default BusinessGlossaryAssociationsTable;
